import {
  Box,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  TableSortLabel,
  Pagination,
  InputAdornment,
} from '@mui/material';
import { PersonAdd, Redo, Search } from '@mui/icons-material';
import { IoTrashOutline as DeleteIcon } from 'react-icons/io5';
import { getProfile, getCurrentOrgId } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Avatar from 'client/modules/App/components/Avatar';

const messages = defineMessages({
  title: {
    id: 'Team.Title',
    defaultMessage: 'Team Management',
  },
  description: {
    id: 'Team.Description',
    defaultMessage: 'Manage your organization members, roles, and pending invitations.',
  },
  teamMembers: {
    id: 'Team.TeamMembers',
    defaultMessage: 'Team Members',
  },
  pendingInvitations: {
    id: 'Team.PendingInvitations',
    defaultMessage: 'Pending Invitations',
  },
  inviteTeammate: {
    id: 'Team.InviteTeammate',
    defaultMessage: 'Invite teammate',
  },
  name: {
    id: 'Team.Name',
    defaultMessage: 'Name',
  },
  email: {
    id: 'Team.Email',
    defaultMessage: 'Email',
  },
  role: {
    id: 'Team.Role',
    defaultMessage: 'Role',
  },
  actions: {
    id: 'Team.Actions',
    defaultMessage: 'Actions',
  },
  owner: {
    id: 'Team.Role.Owner',
    defaultMessage: 'Owner',
  },
  admin: {
    id: 'Team.Role.Admin',
    defaultMessage: 'Admin',
  },
  member: {
    id: 'Team.Role.Member',
    defaultMessage: 'Member',
  },
  viewer: {
    id: 'Team.Role.Viewer',
    defaultMessage: 'Viewer',
  },
  ownerDescription: {
    id: 'Team.Role.Owner.Description',
    defaultMessage: 'Full access to organization settings and billing',
  },
  adminDescription: {
    id: 'Team.Role.Admin.Description',
    defaultMessage: 'Manage team members and organization settings',
  },
  memberDescription: {
    id: 'Team.Role.Member.Description',
    defaultMessage: 'Create and manage surveys and responses',
  },
  viewerDescription: {
    id: 'Team.Role.Viewer.Description',
    defaultMessage: 'View surveys and responses only',
  },
  inviteDialog: {
    id: 'Team.InviteDialog.Title',
    defaultMessage: 'Invite new teammate',
  },
  emailAddress: {
    id: 'Team.InviteDialog.EmailAddress',
    defaultMessage: 'Email address',
  },
  selectRole: {
    id: 'Team.InviteDialog.SelectRole',
    defaultMessage: 'Select role',
  },
  sendInvitation: {
    id: 'Team.InviteDialog.Send',
    defaultMessage: 'Send invitation',
  },
  cancel: {
    id: 'Team.Cancel',
    defaultMessage: 'Cancel',
  },
  resendInvitation: {
    id: 'Team.ResendInvitation',
    defaultMessage: 'Resend invitation',
  },
  deleteInvitation: {
    id: 'Team.DeleteInvitation',
    defaultMessage: 'Delete invitation',
  },
  removeMember: {
    id: 'Team.RemoveMember',
    defaultMessage: 'Remove member',
  },
  changeRole: {
    id: 'Team.ChangeRole',
    defaultMessage: 'Change role',
  },
  invitationSent: {
    id: 'Team.InvitationSent',
    defaultMessage: 'Invitation sent successfully',
  },
  invitationResent: {
    id: 'Team.InvitationResent',
    defaultMessage: 'Invitation resent successfully',
  },
  invitationDeleted: {
    id: 'Team.InvitationDeleted',
    defaultMessage: 'Invitation deleted successfully',
  },
  memberRemoved: {
    id: 'Team.MemberRemoved',
    defaultMessage: 'Member removed successfully',
  },
  roleUpdated: {
    id: 'Team.RoleUpdated',
    defaultMessage: 'Role updated successfully',
  },
  noPermissionToChangeRole: {
    id: 'Team.NoPermissionToChangeRole',
    defaultMessage: "You do not have permission to change this member's role",
  },
  cannotChangeOwnRole: {
    id: 'Team.CannotChangeOwnRole',
    defaultMessage: 'You cannot change your own role',
  },
  noPermissionToChangeRoleTooltip: {
    id: 'Team.NoPermissionToChangeRoleTooltip',
    defaultMessage: "You don't have permission to change this role",
  },
  noTeamMembers: {
    id: 'Team.NoTeamMembers',
    defaultMessage: 'No team members found',
  },
  noPendingInvitations: {
    id: 'Team.NoPendingInvitations',
    defaultMessage: 'No pending invitations',
  },
  statusNever: {
    id: 'Team.Status.Never',
    defaultMessage: 'Never',
  },
  statusOnline: {
    id: 'Team.Status.Online',
    defaultMessage: 'Online',
  },
  statusRecentlyActive: {
    id: 'Team.Status.RecentlyActive',
    defaultMessage: 'Recently active',
  },
  statusOffline: {
    id: 'Team.Status.Offline',
    defaultMessage: 'Offline',
  },
  statusMinutesAgo: {
    id: 'Team.Status.MinutesAgo',
    defaultMessage: '{minutes}m ago',
  },
  statusHoursAgo: {
    id: 'Team.Status.HoursAgo',
    defaultMessage: '{hours}h ago',
  },
  statusDaysAgo: {
    id: 'Team.Status.DaysAgo',
    defaultMessage: '{days}d ago',
  },
  summaryOnline: {
    id: 'Team.Summary.Online',
    defaultMessage: '{count} online',
  },
  summaryRecentlyActive: {
    id: 'Team.Summary.RecentlyActive',
    defaultMessage: '{count} recently active',
  },
  summaryOffline: {
    id: 'Team.Summary.Offline',
    defaultMessage: '{count} offline',
  },
  errorLoadingTeam: {
    id: 'Team.ErrorLoadingTeam',
    defaultMessage: 'Error loading team data',
  },
  errorInviting: {
    id: 'Team.ErrorInviting',
    defaultMessage: 'Error sending invitation',
  },
  sentAt: {
    id: 'Team.SentAt',
    defaultMessage: 'Sent',
  },
  lastSeen: {
    id: 'Team.LastSeen',
    defaultMessage: 'Last Seen',
  },
  teamMembersCount: {
    id: 'Team.TeamMembersCount',
    defaultMessage: 'Team members ({count})',
  },
  searchPlaceholder: {
    id: 'Team.SearchPlaceholder',
    defaultMessage: 'Search team members by name or email...',
  },
});

export default function TeamPage() {
  const dispatch = useDispatch();
  const { formatMessage, locale } = useIntl();
  const profile = useSelector(getProfile);
  const currentOrgId = useSelector(getCurrentOrgId);

  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Sorting state
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination state for members
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Pagination state for invitations
  const [invitePage, setInvitePage] = useState(1);
  const inviteRowsPerPage = 5;

  const roles = [
    {
      value: 'owner',
      label: formatMessage(messages.owner),
      description: formatMessage(messages.ownerDescription),
    },
    {
      value: 'admin',
      label: formatMessage(messages.admin),
      description: formatMessage(messages.adminDescription),
    },
    {
      value: 'member',
      label: formatMessage(messages.member),
      description: formatMessage(messages.memberDescription),
    },
    {
      value: 'viewer',
      label: formatMessage(messages.viewer),
      description: formatMessage(messages.viewerDescription),
    },
  ];

  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [membersResponse, invitationsResponse] = await Promise.all([
        callApi(dispatch, `organizations/${currentOrgId}/members`, null, 'GET'),
        callApi(dispatch, `organizations/${currentOrgId}/invitations`, null, 'GET'),
      ]);
      setMembers(membersResponse.members || []);
      setPendingInvitations(invitationsResponse.invitations || []);
    } catch (err) {
      setError(formatMessage(messages.errorLoadingTeam));
      console.error('Error loading team data:', err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, currentOrgId, formatMessage]);

  useEffect(() => {
    if (currentOrgId) {
      loadTeamData();
    }
  }, [currentOrgId, loadTeamData]);

  const handleInviteTeammate = async () => {
    if (!inviteEmail || !inviteRole) {
      return;
    }

    try {
      setInviteLoading(true);
      setError(null);
      await callApi(dispatch, `organizations/${currentOrgId}/invite`, null, 'POST', {
        email: inviteEmail,
        role: inviteRole,
      });
      setSuccess(formatMessage(messages.invitationSent));
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      loadTeamData(); // Reload to show new pending invitation
    } catch (err) {
      setError(err.message || formatMessage(messages.errorInviting));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvitation = async invitationId => {
    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/invitations/${invitationId}/resend`,
        null,
        'POST',
      );
      setSuccess(formatMessage(messages.invitationResent));
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
    }
  };

  const handleDeleteInvitation = async invitationId => {
    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/invitations/${invitationId}`,
        null,
        'DELETE',
      );
      setSuccess(formatMessage(messages.invitationDeleted));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to delete invitation');
    }
  };

  const handleRemoveMember = async memberId => {
    try {
      await callApi(dispatch, `organizations/${currentOrgId}/members/${memberId}`, null, 'DELETE');
      setSuccess(formatMessage(messages.memberRemoved));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    // Find the member to check permissions
    const targetMember = members.find(m => m._id === memberId);
    if (!targetMember || !canChangeRole(targetMember)) {
      setError(formatMessage(messages.noPermissionToChangeRole));
      return;
    }

    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/members/${memberId}/role`,
        null,
        'PUT',
        {
          role: newRole,
        },
      );
      setSuccess(formatMessage(messages.roleUpdated));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to update role');
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatLastSeen = lastSeen => {
    if (!lastSeen)
      return { text: formatMessage(messages.statusNever), isOnline: false, status: 'offline' };

    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffInMs = now - lastSeenDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 5)
      return { text: formatMessage(messages.statusOnline), isOnline: true, status: 'online' };
    if (diffInMinutes < 30)
      return {
        text: formatMessage(messages.statusRecentlyActive),
        isOnline: false,
        status: 'recent',
      };
    if (diffInMinutes < 60)
      return {
        text: formatMessage(messages.statusMinutesAgo, { minutes: diffInMinutes }),
        isOnline: false,
        status: 'recent',
      };
    if (diffInHours < 24)
      return {
        text: formatMessage(messages.statusHoursAgo, { hours: diffInHours }),
        isOnline: false,
        status: 'today',
      };
    if (diffInDays < 7)
      return {
        text: formatMessage(messages.statusDaysAgo, { days: diffInDays }),
        isOnline: false,
        status: 'week',
      };

    return { text: lastSeenDate.toLocaleDateString(), isOnline: false, status: 'old' };
  };

  // Sorting helper functions
  const handleSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1); // Reset to first page when sorting changes
  };

  // Search functionality
  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when search changes
    setInvitePage(1);
  };

  const filterBySearch = (items, isInvitation = false) => {
    if (!searchQuery.trim()) return items;

    const normalizedQuery = normalizeText(searchQuery);

    return items.filter(item => {
      if (isInvitation) {
        // For invitations, search by email only
        const email = normalizeText(item.email || '');
        return email.includes(normalizedQuery);
      }
      // For members, search by name or email
      const name = normalizeText(item.user?.name || '');
      const email = normalizeText(item.user?.email || '');
      return name.includes(normalizedQuery) || email.includes(normalizedQuery);
    });
  };

  const sortData = (data, comparator) => {
    const stabilizedData = data.map((el, index) => [el, index]);
    stabilizedData.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedData.map(el => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Helper function to normalize text for case-insensitive and accent-insensitive comparison
  const normalizeText = text => {
    if (typeof text !== 'string') return '';
    return text
      .toLowerCase()
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
      .trim();
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue;
    let bValue;

    switch (orderBy) {
      case 'name':
        aValue = normalizeText(a.user?.name || a.user?.email || '');
        bValue = normalizeText(b.user?.name || b.user?.email || '');
        break;
      case 'email':
        aValue = normalizeText(a.user?.email || '');
        bValue = normalizeText(b.user?.email || '');
        break;
      case 'role':
        aValue = normalizeText(a.role || '');
        bValue = normalizeText(b.role || '');
        break;
      case 'lastSeen':
        // For dates, we don't normalize - keep original comparison
        aValue = a.user?.lastSeen || '';
        bValue = b.user?.lastSeen || '';
        break;
      case 'inviteEmail':
        aValue = normalizeText(a.email || '');
        bValue = normalizeText(b.email || '');
        break;
      case 'inviteRole':
        aValue = normalizeText(a.role || '');
        bValue = normalizeText(b.role || '');
        break;
      case 'sentAt':
        // For dates, we don't normalize - keep original comparison
        aValue = a.createdAt || '';
        bValue = b.createdAt || '';
        break;
      default:
        aValue = '';
        bValue = '';
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  };

  // Pagination helper functions
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeInvitePage = (event, newPage) => {
    setInvitePage(newPage);
  };

  // Get filtered, sorted and paginated data
  const filteredMembers = filterBySearch(members, false);
  const sortedMembers = sortData(filteredMembers, getComparator(order, orderBy));
  const paginatedMembers = sortedMembers.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const filteredInvitations = filterBySearch(pendingInvitations, true);
  const sortedInvitations = sortData(filteredInvitations, getComparator(order, orderBy));
  const paginatedInvitations = sortedInvitations.slice(
    (invitePage - 1) * inviteRowsPerPage,
    invitePage * inviteRowsPerPage,
  );

  // Calculate total pages based on filtered data
  const totalPages = Math.ceil(filteredMembers.length / rowsPerPage);
  const totalInvitePages = Math.ceil(filteredInvitations.length / inviteRowsPerPage);

  const currentMember = members.find(m => m.user?.email === profile?.email);
  const isOwnerOrAdmin = currentMember?.role === 'owner' || currentMember?.role === 'admin';

  // Check if current user can change roles for a specific member
  const canChangeRole = targetMember => {
    // Only owners and admins can change roles
    if (!isOwnerOrAdmin) return false;

    // Can't change your own role
    if (targetMember.user?.email === profile?.email) return false;

    // Can't change owner's role
    if (targetMember.role === 'owner') return false;

    return true;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const recentCount = members.filter(
    m => m.user?.lastSeen && moment(m.user?.lastSeen).isAfter(moment().subtract(1, 'month')),
  ).length;

  return (
    <Box>
      <Helmet title={formatMessage(messages.title)} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header with title and search */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {formatMessage(messages.teamMembersCount, { count: members.length })}
          <Chip
            label={formatMessage(messages.summaryRecentlyActive, { count: recentCount })}
            color="success"
            size="small"
            variant="outlined"
          />
        </Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder={formatMessage(messages.searchPlaceholder)}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: searchQuery ? '100%' : 300,
            maxWidth: 400,
            transition: 'width 200ms ease',
            backgroundColor: 'white',
            marginTop: 0,
          }}
        />
      </Box>

      {/* Team Members */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: 'background.body' }}>
            <TableRow>
              <TableCell sx={{ width: '40%', minWidth: 250 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    {formatMessage(messages.name)}
                  </TableSortLabel>
                  <Typography variant="body2" color="text.secondary">
                    /
                  </Typography>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    {formatMessage(messages.email)}
                  </TableSortLabel>
                </Box>
              </TableCell>
              <TableCell sx={{ width: '20%', minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleSort('role')}
                >
                  {formatMessage(messages.role)}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '20%', minWidth: 120 }}>
                <TableSortLabel
                  active={orderBy === 'lastSeen'}
                  direction={orderBy === 'lastSeen' ? order : 'asc'}
                  onClick={() => handleSort('lastSeen')}
                >
                  {formatMessage(messages.lastSeen)}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ width: '20%', minWidth: 120 }}>
                {isOwnerOrAdmin && (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => setInviteDialogOpen(true)}
                    startIcon={<PersonAdd />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {formatMessage(messages.inviteTeammate)}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMembers.map(member => (
              <TableRow key={member._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      name={member.user?.name || member.user?.email || '?'}
                      sx={{ mr: 2, width: 32, height: 32 }}
                      src={member.user?.picture}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={member.user?.name || member.user?.email?.split('@')[0]}
                      >
                        {member.user?.name || member.user?.email?.split('@')[0]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={member.user?.email}
                      >
                        {member.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  {member.role === 'owner' ? (
                    <Chip
                      label={roles.find(r => r.value === member.role)?.label || member.role}
                      color="primary"
                      size="small"
                    />
                  ) : canChangeRole(member) ? (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 140,
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                    >
                      <Select
                        variant="standard"
                        disableUnderline
                        value={member.role}
                        onChange={e => handleChangeRole(member._id, e.target.value)}
                        displayEmpty
                        sx={{
                          height: 32,
                          '& .MuiSelect-select': {
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            display: 'flex',
                            alignItems: 'center',
                          },
                        }}
                      >
                        {roles
                          .filter(r => r.value !== 'owner')
                          .map(role => (
                            <MenuItem key={role.value} value={role.value}>
                              {role.label}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Tooltip
                      title={
                        member.user?.email === profile?.email
                          ? formatMessage(messages.cannotChangeOwnRole)
                          : formatMessage(messages.noPermissionToChangeRoleTooltip)
                      }
                    >
                      <Chip
                        label={roles.find(r => r.value === member.role)?.label || member.role}
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {(() => {
                    const lastSeenInfo = formatLastSeen(member.user?.lastSeen);
                    const exactTime = member.user?.lastSeen
                      ? moment(member.user.lastSeen).locale(locale).format('LLL')
                      : '';

                    return (
                      <Tooltip title={exactTime} placement="top">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lastSeenInfo.isOnline && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: 'success.main',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {!lastSeenInfo.isOnline && lastSeenInfo.status === 'recent' && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: 'warning.main',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {!lastSeenInfo.isOnline && lastSeenInfo.status === 'offline' && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: 'text.disabled',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color={
                              lastSeenInfo.isOnline
                                ? 'success.main'
                                : lastSeenInfo.status === 'recent'
                                  ? 'warning.main'
                                  : lastSeenInfo.status === 'offline'
                                    ? 'text.disabled'
                                    : 'text.secondary'
                            }
                            sx={{ fontWeight: lastSeenInfo.isOnline ? 500 : 400 }}
                          >
                            {lastSeenInfo.text}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  {isOwnerOrAdmin &&
                    member.role !== 'owner' &&
                    member.user?.email !== profile?.email && (
                      <Tooltip title={formatMessage(messages.removeMember)}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(member._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {members.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {formatMessage(messages.noTeamMembers)}
            </Typography>
          </Box>
        )}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        )}
      </TableContainer>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'background.body' }}>
              <TableRow>
                <TableCell sx={{ width: '40%', minWidth: 200 }}>
                  <TableSortLabel
                    active={orderBy === 'inviteEmail'}
                    direction={orderBy === 'inviteEmail' ? order : 'asc'}
                    onClick={() => handleSort('inviteEmail')}
                  >
                    <FormattedMessage
                      id="Team.PendingInvitations"
                      defaultMessage="Pending Invitations"
                    />
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '20%', minWidth: 120 }}>
                  <TableSortLabel
                    active={orderBy === 'inviteRole'}
                    direction={orderBy === 'inviteRole' ? order : 'asc'}
                    onClick={() => handleSort('inviteRole')}
                  >
                    {formatMessage(messages.role)}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '20%', minWidth: 100 }}>
                  <TableSortLabel
                    active={orderBy === 'sentAt'}
                    direction={orderBy === 'sentAt' ? order : 'asc'}
                    onClick={() => handleSort('sentAt')}
                  >
                    {formatMessage(messages.sentAt)}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: '20%', minWidth: 120 }}>
                  {formatMessage(messages.actions)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{ '& .MuiTableRow-root:last-of-type .MuiTableCell-root': { borderBottom: 0 } }}
            >
              {paginatedInvitations.map(invitation => (
                <TableRow key={invitation._id}>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={roles.find(r => r.value === invitation.role)?.label || invitation.role}
                      color={invitation.role === 'owner' ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                  <TableCell align="right">
                    {isOwnerOrAdmin && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title={formatMessage(messages.resendInvitation)}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResendInvitation(invitation._id)}
                          >
                            <Redo />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={formatMessage(messages.deleteInvitation)}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteInvitation(invitation._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {pendingInvitations.length === 0 && !loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {formatMessage(messages.noPendingInvitations)}
              </Typography>
            </Box>
          )}
          {totalInvitePages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={totalInvitePages}
                page={invitePage}
                onChange={handleChangeInvitePage}
                color="primary"
              />
            </Box>
          )}
        </TableContainer>
      )}

      {/* Invite Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{formatMessage(messages.inviteDialog)}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label={formatMessage(messages.emailAddress)}
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>{formatMessage(messages.selectRole)}</InputLabel>
              <Select
                value={inviteRole}
                label={formatMessage(messages.selectRole)}
                onChange={e => setInviteRole(e.target.value)}
              >
                {roles
                  .filter(r => r.value !== 'owner')
                  .map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box>
                        <Typography variant="body1">{role.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={handleInviteTeammate}
            variant="contained"
            disabled={!inviteEmail || !inviteRole || inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={20} /> : <PersonAdd />}
          >
            {formatMessage(messages.sendInvitation)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

TeamPage.loader = store => () => {
  const profile = getProfile(store.getState());
  if (!profile) {
    throw new Response('Unauthorized', { status: 401 });
  }
  return null;
};
